import { money } from '../format.js';
import Peek, { useMeetingContext } from '../Peek.jsx';
import { AdvisoryFeeEditor } from '../Editable.jsx';
import Callouts from '../Callouts.jsx';
import Explainer from '../Explainer.jsx';

// What your investments cost you.
//
// Jake's brief for this page (2026-09-29): the expense ratio and the advisory
// fee, as a percentage and in dollars, because "knowing what someone's paying
// before we tell them what our fee is, is really important." The advisory fee
// is on the statement rather than in the questionnaire, so until the reader
// extracts it the figure is entered here by whoever read the statement. When
// nobody has, the box is not shown at all; it appears in Adjust mode so an
// advisor can fill it in. Pure's own fee is never on this page.

// How to read the page, for a client going through it afterwards. General,
// and it never mentions what Pure charges.
const HOW = [
  {
    title: 'Fund expense ratio',
    text: 'What each fund takes every year to run itself. It comes out of the fund\u2019s returns before you see them, '
      + 'so it never appears as a charge on a statement.',
  },
  {
    title: 'Advisory fee',
    text: 'What an advisor charges to manage the accounts, usually a percentage of their value. '
      + 'It is billed to the account and listed in the fee section of the statement.',
  },
  {
    title: 'Why the percentages matter',
    text: 'Costs are paid every year, whatever the market does, and a dollar paid in cost is a dollar that no longer grows. '
      + 'Over decades a small difference in the yearly rate adds up.',
  },
];

const pct = (v, d = 2) => (Number.isFinite(v) ? `${v.toFixed(d)}%` : 'n/a');
const ROWS = 8;

export default function InvestmentCost({ fees, unpriced, advisory, allIn, callouts = [] }) {
  const { adjustable, adjusting } = useMeetingContext();
  if (!fees) return null;
  const { weightedExpenseRatioPct, annualFeeDollars, perHolding, flags, totalValue } = fees;
  const rows = perHolding.slice(0, ROWS);
  const feeKnown = advisory?.pct !== null && advisory?.pct !== undefined;
  const showFee = feeKnown || (adjustable && adjusting);
  const side = callouts.some(c => !c.hidden);

  return (
    <>
      <div className="conv-module-head">
        <h2>What your investments cost you</h2>
        <span className="conv-quiet">
          {unpriced
            ? `${perHolding.length} priced positions · ${money(totalValue)} of ${money(unpriced.totalValue)}`
            : `${perHolding.length} positions · ${money(totalValue)}`}
        </span>
      </div>

      <div className="conv-tiles">
        <div>
          <span>Fund expense ratio</span>
          <strong>{pct(weightedExpenseRatioPct)}</strong>
          <small>{`${money(annualFeeDollars)} a year, paid from inside the funds`}</small>
        </div>
        {showFee ? (
          <div>
            <span>Advisory fee</span>
            <Peek title="Advisory fee" note="Kept with this assessment, not the questionnaire." saves="assessment" editor={() => <AdvisoryFeeEditor />}>
              <strong>{feeKnown ? pct(advisory.pct) : 'Not recorded'}</strong>
            </Peek>
            <small>{feeKnown && advisory.dollars !== null ? `${money(advisory.dollars)} a year, from your statements` : 'From the fee section of the statements'}</small>
          </div>
        ) : null}
        {allIn ? (
          <div>
            <span>{allIn.complete ? 'All in, every year' : 'At least, every year'}</span>
            <strong className="is-cost">{money(allIn.dollars)}</strong>
            <small>
              {allIn.complete
                ? `${pct(allIn.pct)} of ${money(totalValue)}, whether or not the market moves`
                : `${pct(allIn.pct)} of ${money(totalValue)}; funds with no published ratio are not counted`}
            </small>
          </div>
        ) : null}
      </div>

      <div className={`conv-cost-row${side ? ' has-side' : ''}`}>
        <div>
          <table className="conv-an-table conv-cost-table">
            <thead>
              <tr><th>Holding</th><th>Value</th><th>Weight</th><th>Expense ratio</th><th>Cost a year</th></tr>
            </thead>
            <tbody>
              {rows.map((h, i) => (
                <tr key={`${h.ticker}-${i}`}>
                  <th scope="row"><strong>{h.ticker || '-'}</strong>{h.name ? <span className="conv-quiet">{` · ${h.name}`}</span> : null}</th>
                  <td>{money(h.value)}</td>
                  <td>{pct(h.weightPct, 1)}</td>
                  <td>{h.expenseRatioKnown ? pct(h.expenseRatioPct) : 'n/a'}</td>
                  <td>{h.expenseRatioKnown ? money(h.feeDollars) : 'n/a'}</td>
                </tr>
              ))}
              {perHolding.length > rows.length ? (
                <tr><th scope="row" className="conv-quiet">{`${perHolding.length - rows.length} more positions`}</th><td colSpan={4} /></tr>
              ) : null}
            </tbody>
          </table>
          {flags.unknownExpenseRatioCount > 0 && (
            <p className="conv-caption">
              {`${flags.unknownExpenseRatioCount} position${flags.unknownExpenseRatioCount > 1 ? 's have' : ' has'} no published expense ratio and ${flags.unknownExpenseRatioCount > 1 ? 'are' : 'is'} left out of the average rather than counted as free.`}
            </p>
          )}
          {unpriced?.rows?.length ? (
            <p className="conv-caption">
              {`${money(unpriced.unpricedValue)} could not be priced and sits outside every figure above: ${unpriced.rows.map(u => u.name || u.ticker).filter(Boolean).join(', ')}.`}
            </p>
          ) : null}
        </div>
        <Callouts items={callouts} />
      </div>
      <Explainer title="How to read these costs" items={HOW} />
    </>
  );
}
